import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScreenTitle from '../../common/components/screenTitle/ScreenTitle';
import { useToasts } from 'react-toast-notifications';
import VehiclesService from 'api/vehicles/VehiclesService';
import DriversService from 'api/drivers/DriversService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { LOGGER_LOG_TYPE } from 'Config';
import styles from './DashboardScreen.module.scss';
import ReactTooltip from 'react-tooltip';
import { useSelector } from 'react-redux';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import useDashboardLayout from './hooks/useDashboardLayout';
import { getEnabledCategoriesForUser } from './widgetRegistry';
import { DashboardGroup } from './types/DashboardLayoutTypes';
import DashboardCanvas from './components/DashboardCanvas';

const DashboardScreen = () => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [vehicles, setVehicles] = useState<SelectValueLabel[]>([]);
	const [drivers, setDrivers] = useState<SelectValueLabel[]>([]);
	const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const layoutApi = useDashboardLayout();

	const userPolicies = userProfile?.policies ?? [];
	const dashboardGroup: DashboardGroup = userProfile?.dashboardGroup ?? 'FULL';
	const enabledCategories = useMemo(() => getEnabledCategoriesForUser(userPolicies), [userPolicies]);

	const getData = async () => {
		try {
			Loading.show();
			const [vehiclesList, driversList] = await Promise.all([
				VehiclesService.catalog(),
				DriversService.catalog(),
			]);
			setVehicles(vehiclesList);
			setDrivers(driversList);
		} catch (error) {
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			Logger.error(LOGGER_LOG_TYPE.DASHBOARD, 'error getting dashboard data', error);
		} finally {
			Loading.hide();
		}
	};

	useEffect(() => {
		getData();
	}, []);

	// Lock down ALL outer scroll points so only .scrollArea scrolls
	useEffect(() => {
		const root = document.getElementById('root');
		const scrollHere = document.getElementById('scrollHere');
		const saved = {
			rootOverflow: root?.style.overflowY ?? '',
			scrollOverflow: scrollHere?.style.overflow ?? '',
		};
		if (root) root.style.overflowY = 'clip';
		if (scrollHere) scrollHere.style.overflow = 'hidden';
		return () => {
			if (root) root.style.overflowY = saved.rootOverflow;
			if (scrollHere) scrollHere.style.overflow = saved.scrollOverflow;
		};
	}, []);

	// Painel principal: widgets gerem o seu próprio filtro (filters=undefined),
	// e recebem os catálogos de viaturas/condutores.
	const buildWidgetProps = useMemo(
		() => () => ({ filters: undefined, vehicles, drivers }),
		[vehicles, drivers],
	);

	const hasVisibleWidgets = layoutApi.layout.some(w => w.visible);

	return (
		<ScreenTitle title={t('dashboard.title')}>
			<div className={styles.dashboard}>
				<div className={styles.header}>
					<h1 className={styles.title}>{t('dashboard.title')}</h1>
					<div className={styles.headerButtons}>
						{hasVisibleWidgets && (
							<button
								className={styles.addWidgetButton}
								onClick={() => setIsDrawerOpen(true)}
							>
								{t('dashboard.drawer.widgets_button' as any)}
							</button>
						)}
					</div>
				</div>
				<div className={styles.scrollWrapper}>
					<div className={styles.scrollArea}>
						<ReactTooltip effect="solid" id="main" />
						<DashboardCanvas
							layoutApi={layoutApi}
							userPolicies={userPolicies}
							dashboardGroup={dashboardGroup}
							enabledCategories={enabledCategories}
							buildWidgetProps={buildWidgetProps}
							isDrawerOpen={isDrawerOpen}
							setIsDrawerOpen={setIsDrawerOpen}
						/>
					</div>
				</div>
			</div>
		</ScreenTitle>
	);
};

export default DashboardScreen;
